import React, { useState, useMemo } from "react";
import T from '../../../components/common/T';
import { useInventory } from "../../../context/InventoryContext";
import { useProjects } from "../../../context/ProjectContext";
import { useCostSheets } from "../../../context/CostSheetContext"; 
import { useNavigate, useSearchParams } from "react-router-dom"; // 🟢 Added useSearchParams
import { toast } from "react-toastify";
import { ArrowLeftIcon, ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import "react-toastify/dist/ReactToastify.css";

export default function ScrapReusePage() {
  // 🟢 Brought in scrapList and updateRequisitionStatus
  const { inventories, reuseScrap, getScrapStock, warehouses, scrapList, updateRequisitionStatus } = useInventory();
  const { projects } = useProjects();
  const { costSheets } = useCostSheets(); 
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [form, setForm] = useState({
    scrapOrderId: searchParams.get("orderId") || "", // 🟢 Captures ID
    productId: "",
    quantity: "",
    warehouseId: "",
    bin: "",
    note: "",
    projectId: "",
    costSheetId: "" 
  });

  const availableCostSheets = useMemo(() => {
      if (!form.projectId) return [];
      return costSheets.filter(cs => String(cs.projectId) === String(form.projectId));
  }, [costSheets, form.projectId]);

  const selectedWarehouse = useMemo(() => {
      if (!form.warehouseId) return null;
      return warehouses.find(w => String(w.id) === String(form.warehouseId));
  }, [warehouses, form.warehouseId]);

  const submit = async () => {
    if (!form.productId) return toast.warn("Please select an item to recover.");
    if (!form.quantity || Number(form.quantity) <= 0) return toast.warn("Enter a valid quantity.");
    if (!form.warehouseId || !form.bin) return toast.warn("Please select a Target Warehouse and Bin.");

    const availableScrap = getScrapStock(form.productId);
    if (Number(form.quantity) > availableScrap) {
        return toast.error(`You cannot recover more than ${availableScrap} units.`);
    }
    
    const res = await reuseScrap(form);
    
    if (res.success) {
        // 🟢 MAGIC FIX: Update the specific order's status!
        if (form.scrapOrderId) {
            await updateRequisitionStatus(form.scrapOrderId, "RECOVERED");
        }

        toast.success(res.message || "Scrap Reused Successfully!");
        setTimeout(() => navigate("/admin/material/scrap"), 1500);
    } else {
        toast.error(res.message);
    }
  };

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen font-sans flex items-center justify-center">
        <div className="w-full max-w-2xl bg-white shadow-lg border border-gray-200 rounded-lg overflow-hidden">
            
            <div className="bg-gray-50 px-8 py-6 border-b border-gray-200 flex justify-between items-center">
                <div>
                    <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                        <ArrowUturnLeftIcon className="w-6 h-6 text-green-600" />
                        Recover Material
                    </h2>
                    <p className="text-sm text-gray-500 mt-1"><T>Move items from </T><b><T>Virtual/Scrap</T></b><T> back to </T><b><T>WH/Stock</T></b>.</p>
                </div>
                <button onClick={() => navigate("/admin/material/scrap")} className="text-gray-400 hover:text-gray-600 transition">
                    <ArrowLeftIcon className="w-6 h-6" />
                </button>
            </div>

            <div className="p-8 space-y-6">
                
                {/* 🟢 NEW: LINK TO SOURCE ORDER */}
                <div className="bg-purple-50 border border-purple-100 p-4 rounded-md">
                    <label className="block text-xs font-bold text-purple-700 uppercase mb-1"><T>Link to Scrap Order (Optional)</T></label>
                    <select
                      className="w-full border border-purple-200 p-2.5 rounded bg-white focus:ring-2 focus:ring-purple-500 outline-none"
                      value={form.scrapOrderId}
                      onChange={e => setForm({ ...form, scrapOrderId: e.target.value })}
                    >
                      <option value=""><T>General Pool Recovery (Do not link to specific order)</T></option>
                      {scrapList.filter(s => s.status === 'RECEIVED').map(s => (
                        <option key={s.id} value={s.id}>Order #{s.id} - {s.reference_number || s.id} ({s.employee_name})</option>
                      ))}
                    </select>
                    <p className="text-xs text-purple-600 mt-2">
                        * If linked, the specific order's status will automatically change to <b><T>RECOVERED</T></b>.
                    </p>
                </div>

                {/* 1. Item Selection */}
                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Scrap Item to Recover</T></label>
                    <select
                      className="w-full border border-gray-300 p-2.5 rounded bg-white focus:ring-2 focus:ring-green-500 outline-none transition"
                      value={form.productId}
                      onChange={e => setForm({ ...form, productId: e.target.value })}
                    >
                      <option value=""><T>Select Scrap Item...</T></option>
                      {inventories.map(i => {
                          const scrapQty = getScrapStock(i.id);
                          if (scrapQty <= 0) return null; 
                          return (
                            <option key={i.id} value={i.id}>
                              {i.itemName} (Available: {scrapQty} {i.unit || 'Units'})
                            </option>
                          )
                      })}
                    </select>
                </div>

                {/* 2. Project Credit Selection */}
                <div className="bg-blue-50 border border-blue-100 p-4 rounded-md">
                    <label className="block text-xs font-bold text-blue-700 uppercase mb-1"><T>Project to Credit (Optional)</T></label>
                    <div className="grid grid-cols-2 gap-4">
                        <select
                          className="w-full border border-blue-200 p-2.5 rounded bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                          value={form.projectId}
                          onChange={e => setForm({ ...form, projectId: e.target.value, costSheetId: "" })}
                        >
                          <option value=""><T>Do not credit any project</T></option>
                          {projects.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        
                        <select
                          className="w-full border border-blue-200 p-2.5 rounded bg-white focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100 disabled:text-gray-400"
                          value={form.costSheetId}
                          onChange={e => setForm({ ...form, costSheetId: e.target.value })}
                          disabled={!form.projectId}
                        >
                          <option value="">{form.projectId ? "Select Job Cost Sheet" : "Select Project First"}</option>
                          {availableCostSheets.map(cs => (
                            <option key={cs.id} value={cs.id}>{cs.name}</option>
                          ))}
                        </select>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                        * Value will be added as a <b><T>Credit (Negative Cost)</T></b> to the selected Cost Sheet.
                    </p>
                </div>

                {/* 3. Quantity & Destination */}
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Quantity</T></label>
                        <input
                          type="number"
                          className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-green-500 outline-none"
                          placeholder="0.00"
                          value={form.quantity}
                          onChange={e => setForm({ ...form, quantity: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Target Warehouse</T></label>
                        <select 
                          className="w-full border border-gray-300 p-2.5 rounded bg-white focus:ring-2 focus:ring-green-500 outline-none" 
                          value={form.warehouseId} 
                          onChange={e => setForm({...form, warehouseId: e.target.value, bin: ""})} 
                        >
                            <option value="">Select Warehouse...</option>
                            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Bin / Shelf</T></label>
                        <select 
                          className="w-full border border-gray-300 p-2.5 rounded bg-white focus:ring-2 focus:ring-green-500 outline-none disabled:bg-gray-100 disabled:text-gray-400" 
                          value={form.bin} 
                          onChange={e => setForm({...form, bin: e.target.value})}
                          disabled={!form.warehouseId} 
                        >
                            <option value="">Select Bin...</option>
                            {selectedWarehouse?.bins?.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                    </div>
                </div>

                {/* 4. Notes */}
                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Reason / Note</T></label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="e.g. Mistakenly scrapped, cleaned and reused"
                      value={form.note}
                      onChange={e => setForm({ ...form, note: e.target.value })}
                    />
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
                    <button onClick={() => navigate("/admin/material/scrap")} className="px-5 py-2.5 border border-gray-300 rounded text-gray-600 font-bold text-xs uppercase hover:bg-gray-50 transition">
                        Cancel
                    </button>
                    <button onClick={submit} className="bg-green-600 text-white px-6 py-2.5 rounded shadow-md hover:bg-green-700 font-bold uppercase text-xs transition transform active:scale-95">
                        Confirm Recovery
                    </button>
                </div>
            </div>
        </div>
      </div>
    </>
  );
}